import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ClipboardCheck, CalendarCheck } from "lucide-react";

/**
 * Welcome hero on the homepage — client-focused. Copy speaks to filing
 * clients only; tax-pro paths live in the footer / nav for those who need
 * them. The refund calculator + book + intake CTAs live directly below on
 * the Home page so a first-time visitor never has to click twice.
 */
export default function WelcomeMessage() {
  return (
    <section
      className="relative border-b border-border overflow-hidden bg-gradient-to-b from-secondary/40 to-background"
      data-testid="home-welcome-message"
    >
      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 md:gap-14 items-center px-6 lg:px-12 py-14 md:py-20">
        {/* Image column */}
        <div className="relative order-1 md:order-2">
          <div
            className="absolute -inset-6 rounded-full bg-primary/15 blur-3xl"
            aria-hidden="true"
          />
          <div className="relative rounded-lg overflow-hidden border border-border shadow-xl bg-card animate-[fadeIn_0.9s_ease-out]">
            <img
              src="/welcome_hero.png"
              alt="Welcome to DyNamic Tax"
              className="w-full h-auto object-cover"
              data-testid="home-welcome-hero-img"
              loading="eager"
            />
          </div>
        </div>

        {/* Copy column */}
        <div className="order-2 md:order-1 animate-[slideInLeft_0.9s_ease-out]">
          <div className="label-caps mb-3 text-primary">A warm welcome</div>
          <h2 className="font-serif text-4xl md:text-5xl leading-tight mb-5">
            Ready to file your return —{" "}
            <span className="italic text-primary">from anywhere in America?</span>
          </h2>
          <p className="text-muted-foreground text-lg leading-relaxed mb-4">
            <strong className="text-foreground">DyNamic Tax</strong> files individual and business returns 100% virtually, all 50 states.
          </p>
          <div className="space-y-3 text-sm text-foreground/85 leading-relaxed mb-8">
            <p>
              Estimate your refund right on this page, upload your paperwork, and book a video appointment — we handle the rest.
            </p>
            <p className="italic text-primary">
              Fast. Virtual. Accurate. That's the DyNamic difference.
            </p>
          </div>

          {/* Two client-focused CTAs — jump-links to the tools below */}
          <div className="flex flex-wrap gap-3">
            <Button asChild size="lg" data-testid="welcome-cta-intake">
              <Link to="/intake"><ClipboardCheck size={16} className="mr-2" /> Start intake form <ArrowRight size={14} className="ml-2" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" data-testid="welcome-cta-book">
              <Link to="/book"><CalendarCheck size={16} className="mr-2" /> Book appointment</Link>
            </Button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: scale(0.98); } to { opacity: 1; transform: scale(1); } }
        @keyframes slideInLeft { from { opacity: 0; transform: translateX(-14px); } to { opacity: 1; transform: translateX(0); } }
      `}</style>
    </section>
  );
}
